import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
    Warehouse,
    Truck,
    HardHat,
    ListChecks,
    BookOpen,
    Gear,
    House,
    SignOut,
    List,
    X,
    User,
    ShieldCheck,
    ChatsCircle,
    Database,
    Table,
    Wrench,
    Package,
    Users,
    Calendar,
    Clipboard,
    FolderOpen,
    ClockCounterClockwise
} from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Map of icon names to components
const iconMap = {
    "warehouse": Warehouse,
    "truck": Truck,
    "hardhat": HardHat,
    "listchecks": ListChecks,
    "bookopen": BookOpen,
    "gear": Gear,
    "house": House,
    "database": Database,
    "table": Table,
    "wrench": Wrench,
    "package": Package,
    "users": Users,
    "calendar": Calendar,
    "clipboard": Clipboard,
    "folder": FolderOpen,
};

// Default/fixed menu items
const fixedNavItems = [
    { path: "/", label: "Dashboard", icon: House, fixed: true },
    { path: "/diario", label: "Diario de Obra", icon: BookOpen, fixed: true },
    { path: "/mensajeria", label: "Mensajería", icon: ChatsCircle, fixed: true },
    { path: "/configuracion", label: "Configuración", icon: Gear, fixed: true },
];

// Map collection names to their specific routes and icons
const collectionRouteMap = {
    "tools": { path: "/inventario", icon: Warehouse },
    "vehicles": { path: "/vehiculos", icon: Truck },
    "projects": { path: "/obras", icon: HardHat },
    "tasks": { path: "/tareas", icon: ListChecks },
    "daily_logs": { path: "/diario", icon: BookOpen },
};

const Layout = ({ children }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [dynamicNavItems, setDynamicNavItems] = useState([]);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    // Fetch collections for dynamic menu
    useEffect(() => {
        const fetchCollections = async () => {
            try {
                const response = await axios.get(`${API}/schemas`);
                const collections = response.data;
                
                // Build dynamic nav items from collections
                const items = collections
                    .filter(col => col.show_in_menu !== false) // Show by default unless explicitly hidden
                    .map(col => {
                        const routeConfig = collectionRouteMap[col.name];
                        const iconName = col.menu_icon || "database";
                        const IconComponent = iconMap[iconName] || Database;
                        
                        return {
                            path: routeConfig?.path || `/coleccion/${col.name}`,
                            label: col.menu_name || col.display_name || col.name,
                            icon: routeConfig?.icon || IconComponent,
                            collectionName: col.name,
                            order: col.menu_order ?? 99,
                        };
                    })
                    .sort((a, b) => a.order - b.order);
                
                setDynamicNavItems(items);
            } catch (error) {
                console.error("Error fetching collections for menu:", error);
            }
        };

        if (user) {
            fetchCollections();
        }
    }, [user]);

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    // Combine fixed items with dynamic items
    // Dashboard first, then dynamic collections, then fixed utilities at the end
    const dashboardItem = fixedNavItems.find(i => i.path === "/");
    const utilityItems = fixedNavItems.filter(i => i.path !== "/" && i.path !== "/diario");
    const diarioItem = fixedNavItems.find(i => i.path === "/diario");
    
    // Filter out daily_logs from dynamic items since we have a fixed Diario de Obra
    const filteredDynamicItems = dynamicNavItems.filter(i => i.collectionName !== "daily_logs");
    
    const allNavItems = [
        dashboardItem,
        ...filteredDynamicItems,
        { path: "/reparto", label: "Reparto", icon: Users },
        { path: "/historico", label: "Histórico", icon: ClockCounterClockwise },
        diarioItem,
        ...utilityItems,
        { path: "/admin", label: "Administración", icon: ShieldCheck, adminOnly: true },
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            {/* Mobile Header */}
            <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E5E5E5] px-4 py-3">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                        data-testid="mobile-menu-btn"
                    >
                        <List size={24} weight="bold" />
                    </button>
                    <h1 className="font-heading font-bold text-lg">Gestión de Obras - THC</h1>
                    <DropdownMenu>
                        <DropdownMenuTrigger className="p-2 hover:bg-[#F8F9FA] rounded-sm">
                            <User size={24} weight="bold" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleLogout}>
                                <SignOut className="mr-2" size={18} />
                                Cerrar sesión
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-50 bg-black/50"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-[#E5E5E5] transform transition-transform duration-200 lg:translate-x-0 ${
                    sidebarOpen ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="sidebar"
            >
                <div className="p-6 border-b border-[#E5E5E5]">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#FFD700] rounded-sm flex items-center justify-center">
                                <HardHat size={24} weight="fill" className="text-[#111111]" />
                            </div>
                            <span className="font-heading font-bold text-xl">Gestión de Obras - THC</span>
                        </div>
                        <button
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden p-1 hover:bg-[#F8F9FA] rounded-sm"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <nav className="p-4 space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
                    {allNavItems
                        .filter(item => !item.adminOnly || user?.role === "admin")
                        .map((item) => {
                            const Icon = item.icon;
                            const isActive = location.pathname === item.path || 
                                (item.collectionName && location.pathname === `/coleccion/${item.collectionName}`);
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`sidebar-link ${isActive ? "active" : ""} ${item.adminOnly ? "text-red-600" : ""}`}
                                    data-testid={`nav-${item.path.replace("/", "").replace("/", "-") || "dashboard"}`}
                                >
                                    <Icon size={20} weight={isActive ? "fill" : "regular"} />
                                    <span>{item.label}</span>
                                </Link>
                            );
                        })}
                </nav>

                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E5E5E5]">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-[#0055FF] rounded-sm flex items-center justify-center text-white font-bold">
                            {user?.name?.charAt(0).toUpperCase() || "U"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{user?.name}</p>
                            <p className="text-xs text-[#555555] truncate">{user?.company || user?.email}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-[#555555] hover:text-[#111111] hover:bg-[#F8F9FA] rounded-sm transition-colors"
                        data-testid="logout-btn"
                    >
                        <SignOut size={18} />
                        Cerrar sesión
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
                <div className="p-4 md:p-6 lg:p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
